import {
  Component,
  ChangeDetectionStrategy,
  signal,
  inject,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { distinctUntilChanged } from 'rxjs/operators';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { ReportService } from '../../services/report.service';
import { Router } from '@angular/router';
import { Loader } from '../../loader/loader';
import { SnackbarService } from '../../services/snackbar.service';
import { ReportSharedService } from '../../services/report-shared';
import { GetProjectFilesRequest } from '../../models/get-project-files-request';
import { ValidationMessagesTexts } from '../../constants/validation-messages.constants';
import { ApiResponseMessages } from '../../constants/api-response-messages.constants';
import { DialogueTexts } from '../../constants/dialog-texts.constant';
import { NumbersOnlyDirective } from '../../directives/numbers-only';
import { MonthPickerComponent } from '../../components/month-picker/month-picker';
import { SearchableDropdown } from '../../components/searchable-dropdown/searchable-dropdown';

@Component({
  selector: 'app-create-report-type',
  templateUrl: './create-report-type.html',
  styleUrls: ['./create-report-type.scss'],
  imports: [
    MatStepperModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    CommonModule,
    Loader,
    NumbersOnlyDirective,
    MonthPickerComponent,
    SearchableDropdown,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateReportTypeComponent {
  constructor(
    private router: Router,
    private cd: ChangeDetectorRef,
  ) {
  }
  @ViewChild('stateSelect') stateSelect!: ElementRef;
  private fb = inject(FormBuilder);
  private reportService = inject(ReportService);
  private snackbar = inject(SnackbarService);
  private reportSharedService = inject(ReportSharedService);
  isNextButtonDiabled: boolean = true;
  fileId: string = '';
  referencePath: string = '';
  validationMessages = ValidationMessagesTexts;
  apiResponseMessages = ApiResponseMessages;
  isMessageDialogOpen = false;
  filterErrorMessages: string = '';
  dialogTexts = DialogueTexts;

  reportTypes = this.reportService.getReportTypes();
  clients = this.reportService.getClients();

  isLoading = false;
  searchResults = signal<any[]>([]);

  satates = this.reportService.getStates();
  locations = this.reportService.getLocations();

  options: FormGroup = this.fb.group({
    reportType: ['', [Validators.required]],
    projectID: ['', [Validators.required, Validators.maxLength(8)]],
    sitelocationname: [''],
    clientName: ['', [Validators.required]],
    reportMonth: [''],
    stateName: [''],
  });

  private clientsEffect = effect(() => {
    const clientList = this.clients();
    if (clientList.length > 0 && !this.options.get('clientName')?.value) {
      this.options.get('clientName')?.setValue(clientList[0].clientId);
    }
  });

  private reportTypesEffect = effect(() => {
    const typeList = this.reportTypes();
    if (typeList.length > 0 && !this.options.get('reportType')?.value) {
      this.options.get('reportType')?.setValue(typeList[0].id);
    }
  });

  ngOnInit(): void {
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    this.options.get('clientName')?.valueChanges.pipe(distinctUntilChanged()).subscribe((clientId) => {
      if (clientId) {
        this.options.get('sitelocationname')?.setValue('');
        this.options.get('stateName')?.setValue('');
        this.options.get('reportType')?.setValue('');
        this.reportService.loadReportTypes(clientId);
        this.reportService.loadStates(clientId);
        this.reportService.loadLocations(clientId);
      }
    });
  }

  ngOnDestroy(): void {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  closeMessageDialog() {
    this.isMessageDialogOpen = false;
  }

  selectedFileId: string | null = null;

  selectReport(report: any) {
    this.selectedFileId = report.fileId;
    this.isNextButtonDiabled = false;
    this.fileId = report.fileId;
    this.referencePath = report.basePdfFilePath;
  }

  step = signal(1);

  setStep(newStep: number) {
    this.step.set(newStep);
  }

  findReports(event?: Event): void {
    this.options.markAllAsTouched();
    if (this.options.invalid) {
      this.isLoading = false;
      return;
    }
    if (event) {
      event.preventDefault();
    }
    const values = this.options.getRawValue();
    const selectedLocation = this.locations().find(
      (loc) => loc.locationId === Number(values.sitelocationname),
    );
    const selectedState = this.satates().find((loc) => loc.stateId === Number(values.stateName));
    const selectedClient = this.clients().find((loc) => loc.clientId === Number(values.clientName));
    const selectedreportType = this.reportTypes().find(
      (type) => type.id === Number(values.reportType),
    );
    this.isLoading = true;
    const request: GetProjectFilesRequest = {
      client: selectedClient?.clientName ?? '',
      state: selectedState?.stateName ?? '',
      location: selectedLocation?.locationName ?? '',
      project_id: values.projectID?.toString() ?? '',
      reportMonth: this.formatMonth(values.reportMonth) ?? '',
      reportType: selectedreportType?.reportTypeName ?? '',
      reportTypeId: Number(values.reportType),
    };
    this.reportService.findProjectFiles(request).subscribe({
      next: (reports) => {
        if (!reports?.success) {
          this.isLoading = false;
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: reports?.message ?? this.apiResponseMessages.NO_REPORT_FOUND,
          });
          this.searchResults.set([]);
          return;
        }
        if (!reports.data || (Array.isArray(reports.data) && reports.data.length === 0)) {
          this.isLoading = false;
          this.isMessageDialogOpen = true;
          this.filterErrorMessages = reports.message.replace(/[,]\s*/g, '');
          this.filterErrorMessages =
            this.filterErrorMessages ?? this.apiResponseMessages.NO_REPORT_FOUND;

          this.searchResults.set([]);
          return;
        }

        this.searchResults.set(reports.data);
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.cd.markForCheck();
        const apiMessage = error?.error?.message ?? this.apiResponseMessages.NO_REPORT_FOUND;
        if (error.status === 404) {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: apiMessage,
          });
          this.searchResults.set([]);
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
          });
        }
      },
    });
  }

  formatMonth(value: string): string {
    if (!value) return '';
    const [year, month] = value.split('-');
    const date = new Date(Number(year), Number(month) - 1);
    return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  }

  onCancel(): void {
    this.router.navigate(['/reports']);
  }
  onReset(): void {
    this.isNextButtonDiabled = true;
    this.selectedFileId = null;
    this.reportService.clearClientDependentData();
    this.options.reset();
    this.options.get('stateName')?.setValue('');
    this.options.get('sitelocationname')?.setValue('');
    this.options.get('clientName')?.setValue('');
    this.options.get('projectID')?.setValue('');
    this.options.get('reportMonth')?.setValue('');
    this.options.get('reportType')?.setValue('');
    this.searchResults.set([]);
  }

  generateReport() {
    if (this.options.valid) {
      const values = this.options.getRawValue();
      const finalPayload = {
        ...values,
        fileId: this.fileId,
        referencePath: this.referencePath,
      };
      this.reportSharedService.setFormData(finalPayload);
      this.router.navigate(['/generate-report']);
    }
  }

  previewDoc(fileId: string, documentUrl: string, reportname: string) {
    this.router.navigate(['/preview-report'], {
      queryParams: {
        fileId: fileId,
        documentUrl: encodeURIComponent(documentUrl),
        reportname: reportname,
        downloadOverrideUrl: '',
      },
      state: { fromPdfViewer: true, returnUrl: this.router.url },
    });
  }
}
